import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { hasAuthParams, useAuth } from "react-oidc-context";

import { clearPersistedAuthState, isLoginEnabled } from "@/api/authSession";
import { getUserManager } from "@/api/userManager";
import { isInIframe } from "@/base/utils/iframeUtils";
import { hasData, isLoading } from "@/store/apiRequestStatusMachine";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logInWithID, selectIsAuthReady, selectUserState, setGuest } from "@/store/user";

import type { UserProviderProps } from "./types";
import { UserContext } from "./useUser";

let hasClearedDisabledAuthSession = false;

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const auth = useAuth();
  const [hasTriedSilentSignin, setHasTriedSilentSignin] = React.useState(false);
  const isAuthReady = useAppSelector(selectIsAuthReady);
  const userState = useAppSelector(selectUserState);
  const signinSilent = auth.signinSilent;

  const shouldTrySilentSignin =
    !isInIframe() && !hasAuthParams() && isLoginEnabled();

  const hasOidcUser = auth.isAuthenticated && Boolean(auth.user) && !auth.user?.expired;

  const setGuestState = useCallback(() => {
    dispatch(setGuest());
  }, [dispatch]);

  const clearAuthSession = useCallback(async () => {
    setGuestState();
    try {
      await auth.removeUser();
    } catch {
      // Keep app state synced even if OIDC store cleanup fails.
    }
  }, [auth, setGuestState]);

  const bootstrapUser = useCallback(async () => {
    await dispatch(logInWithID({ body: undefined }));
  }, [dispatch]);

  const clearAuthSessionIfUserInvalid = useCallback(async () => {
    try {
      const currentUser = await getUserManager(i18n.language).getUser();

      if (!currentUser || currentUser.expired) {
        await clearAuthSession();
      }
    } catch {
      // Keep the current session if the OIDC store can't be read.
      return;
    }
  }, [clearAuthSession, i18n.language]);

  // Clean up stale OIDC request state entries from localStorage
  useEffect(() => {
    if (!isLoginEnabled()) {
      return;
    }

    void getUserManager(i18n.language).clearStaleState();
  }, [i18n.language]);

  useEffect(() => {
    if (isLoginEnabled() || hasClearedDisabledAuthSession) {
      return;
    }

    hasClearedDisabledAuthSession = true;
    clearPersistedAuthState();
    setGuestState();

    void auth.removeUser().catch(() => {
      // Keep app state synced even if OIDC store cleanup fails.
    });
  }, [auth.removeUser, setGuestState]);

  useEffect(() => {
    if (
      !shouldTrySilentSignin ||
      hasOidcUser ||
      auth.activeNavigator ||
      auth.isLoading ||
      hasTriedSilentSignin
    ) {
      return;
    }

    void (async () => {
      try {
        await signinSilent();
      } catch {
        // Silent signin failed, app will continue as guest.
      } finally {
        void getUserManager(i18n.language).clearStaleState();
        setHasTriedSilentSignin(true);
      }
    })();
  }, [
    auth.activeNavigator,
    auth.isLoading,
    hasOidcUser,
    hasTriedSilentSignin,
    i18n.language,
    signinSilent,
    shouldTrySilentSignin,
  ]);

  useEffect(() => {
    if (!isLoginEnabled() || auth.isLoading || isAuthReady || !hasOidcUser) {
      return;
    }

    if (isLoading(userState)) {
      return;
    }

    void bootstrapUser();
  }, [auth.isLoading, bootstrapUser, hasOidcUser, isAuthReady, userState]);

  useEffect(() => {
    if (!isLoginEnabled() || auth.isLoading || hasOidcUser || isAuthReady) {
      return;
    }

    if (shouldTrySilentSignin && !hasTriedSilentSignin) {
      return;
    }

    setGuestState();
  }, [
    auth.isLoading,
    hasOidcUser,
    hasTriedSilentSignin,
    isAuthReady,
    setGuestState,
    shouldTrySilentSignin,
  ]);

  // Keep Redux/local storage in sync with OIDC token/session lifecycle.
  useEffect(() => {
    if (!isLoginEnabled()) {
      return;
    }

    const cleanupCallbacks = [
      auth.events.addAccessTokenExpired(() => {
        void clearAuthSession();
      }),
      auth.events.addUserSignedOut(() => {
        void clearAuthSessionIfUserInvalid();
      }),
      auth.events.addUserUnloaded(() => {
        setGuestState();
      }),
      auth.events.addSilentRenewError(() => {
        void clearAuthSessionIfUserInvalid();
      }),
    ];

    return () => {
      for (const cleanup of cleanupCallbacks) {
        cleanup();
      }
    };
  }, [
    auth.events,
    clearAuthSession,
    clearAuthSessionIfUserInvalid,
    setGuestState,
  ]);

  const login = async () => {
    if (!isLoginEnabled() || auth.isLoading || auth.activeNavigator) {
      return;
    }

    // Skip manual bootstrap while OIDC auth is still settling, while the
    // existing bootstrap request is still loading, or once Redux already has
    // user data. Only call bootstrapUser() when hasOidcUser is true and Redux
    // is ready for a recovery retry.
    if (hasOidcUser) {
      if (!isAuthReady || isLoading(userState) || hasData(userState)) {
        return;
      }

      await bootstrapUser();
      return;
    }

    await auth.signinRedirect();
  };

  const logout = async () => {
    const userManager = getUserManager(i18n.language);

    try {
      await userManager.clearStaleState();
    } catch {
      // Continue logout even if request-state cleanup fails.
    }

    try {
      await auth.signoutRedirect({
        post_logout_redirect_uri: globalThis.location.origin,
      });
    } catch (error) {
      console.error("Failed to start sign-out redirect.", error);
      await clearAuthSession();
    }
  };

  return (
    <UserContext.Provider
      value={{
        login,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
